import { useMemo, useState, useEffect } from "react";
import Sidebar from "../sidebar/Sidebar";
import Footer from "../components/Footer";
import { AiFillLinkedin, AiFillInstagram } from "react-icons/ai";
import { SiGooglescholar, SiOrcid, SiNotion } from "react-icons/si";
import { IoMdMail } from "react-icons/io";
import { FaMedal } from "react-icons/fa";
import { publications } from "../data/publications";
import { collaborators } from "../data/collaborators";
import { mentoring } from "../data/mentoring";
import { mentalHealthWork } from "../data/mentalhealthwork";
import { researchPillars } from "../data/researchPillars";
import PublicationCard from "../components/PublicationCard";
import MentalHealthCard from "../components/MentalHealthCard";
import HomeBio from "../components/HomeBio";
import NewsUpdates from "../components/NewsUpdates";
import Features from "../components/Features";
import ResearchSection from "../components/ResearchSection";
import { acronymTooltips } from "../data/acronymTooltips";
import {
  introLinks,
  homePitch,
  academicServices,
  awardsAndHonours,
  personalInterestParagraphs,
  pageCopy,
} from "../data/homeContent";
import kellieMainImg from "../images/me/kellie-main.JPEG";
import PageContainer from "../components/PageContainer";
import EditableRegion from "../studio/EditableRegion";
import { useStudio } from "../studio/StudioContext";

/** Leading 🏅 in content marks an awarded line; render as medal icon instead of emoji. */
const parseAwardedLabel = (raw = "") => {
  const text = String(raw);
  const awarded = /^\s*🏅\s*/.test(text);
  return {
    awarded,
    label: text.replace(/^\s*🏅\s*/, ""),
  };
};

const AwardedLabel = ({
  children,
  awarded = false,
  medalPosition = "end",
  className = "",
}) => {
  const medal = awarded ? (
    <FaMedal
      size={12}
      className={`inline-block align-[-0.1em] text-amber-700 dark:text-amber-300 ${
        medalPosition === "start" ? "mr-1.5" : "ml-1.5"
      }`}
      aria-hidden="true"
      title="Recognised"
    />
  ) : null;

  if (!awarded) {
    return <span className={className}>{children}</span>;
  }

  if (medalPosition === "start") {
    return (
      <span className={className}>
        {medal}
        {children}
      </span>
    );
  }

  const text = String(children ?? "");
  const lastSpace = text.lastIndexOf(" ");
  const lead = lastSpace === -1 ? "" : text.slice(0, lastSpace + 1);
  const last = lastSpace === -1 ? text : text.slice(lastSpace + 1);

  return (
    <span className={className }>
      {lead}
      <span className="whitespace-nowrap">
        {last}
        {medal}
      </span>
    </span>
  );
};

const Home = () => {
  const SHOW_ALL_KEYWORD = "Show all";
  const FEATURED_KEYWORD = "Featured";
  const { studioMode, draft } = useStudio();
  const pitch =
    studioMode && draft?.homePitch ? draft.homePitch : homePitch;
  const awards =
    studioMode && draft?.awardsAndHonours
      ? draft.awardsAndHonours
      : awardsAndHonours;
  const pillarsForCards =
    studioMode && draft?.researchPillars
      ? draft.researchPillars
      : researchPillars;
  const copy =
    studioMode && draft?.pageCopy
      ? { ...pageCopy, ...draft.pageCopy }
      : pageCopy;
  const pubsSource =
    studioMode && draft?.publications ? draft.publications : publications;
  const mentoringSource =
    studioMode && draft?.mentoring ? draft.mentoring : mentoring;
  const collaboratorsSource =
    studioMode && draft?.collaborators ? draft.collaborators : collaborators;
  const services =
    studioMode && draft?.academicServices
      ? draft.academicServices
      : academicServices;
  const interests =
    studioMode && draft?.personalInterestParagraphs
      ? draft.personalInterestParagraphs
      : personalInterestParagraphs;
  const iconMap = {
    mail: IoMdMail,
    linkedin: AiFillLinkedin,
    instagram: AiFillInstagram,
    scholar: SiGooglescholar,
    orcid: SiOrcid,
    notion: SiNotion,
  };

  const publicationKeywords = useMemo(() => {
    const keywordSet = new Set();
    pubsSource.forEach((pub) => {
      if (pub.selected) keywordSet.add(FEATURED_KEYWORD);
      (pub.keywords || []).forEach((keyword) => keywordSet.add(keyword));
    });
    const keywordList = Array.from(keywordSet).filter(
      (keyword) => keyword !== FEATURED_KEYWORD && keyword !== SHOW_ALL_KEYWORD,
    );
    const ordered = [];
    if (keywordSet.has(FEATURED_KEYWORD)) ordered.push(FEATURED_KEYWORD);
    ordered.push(...keywordList);
    ordered.push(SHOW_ALL_KEYWORD);
    return ordered;
  }, [pubsSource]);

  // determine initial keyword: 'Featured' if it exists, else 'Show all'
  const determineInitialKeyword = publicationKeywords => {
    if (publicationKeywords.includes(FEATURED_KEYWORD)) {
      return FEATURED_KEYWORD;
    }
    return SHOW_ALL_KEYWORD;
  };

  const [activeKeyword, setActiveKeyword] = useState(() =>
    determineInitialKeyword(publicationKeywords)
  );

  useEffect(() => {
    const scrollToHashTarget = () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(window.location.search);
      let targetId = null;
      if (hash === "#mental-health") {
        targetId = "mental-health";
      } else if (
        hash === "#research-agenda" ||
        params.has("view") ||
        params.has("pillar") ||
        params.has("project")
      ) {
        targetId = "research-agenda";
      }
      if (!targetId) return;
      const el = document.getElementById(targetId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    scrollToHashTarget();
    window.addEventListener("hashchange", scrollToHashTarget);
    return () => window.removeEventListener("hashchange", scrollToHashTarget);
  }, []);

  // If publications data or publicationKeywords changes (on hydration or data load), update initialKeyword only if the activeKeyword is not currently a valid keyword anymore.
  useEffect(() => {
    // If the activeKeyword is not in the new publicationKeywords, fallback to newly-determined initial keyword (Featured if present, else Show all)
    if (!publicationKeywords.includes(activeKeyword)) {
      setActiveKeyword(determineInitialKeyword(publicationKeywords));
    }
    // We intentionally do not update activeKeyword on every keyword change to not override user's manual selection
    // eslint-disable-next-line
  }, [publicationKeywords]);

  // const projectSummary = useMemo(
  //   () => [
  //     {
  //       label: "Research projects",
  //       count: researchProjects.length,
  //       href: "/research",
  //     },
  //     {
  //       label: "School projects",
  //       count: schoolProjects.length,
  //       href: "/projects",
  //     },
  //     {
  //       label: "Other projects",
  //       count: otherProjects.length,
  //       href: "/projects",
  //     },
  //   ],
  //   [],
  // );
  const filteredPublications = useMemo(() => {
    if (activeKeyword === FEATURED_KEYWORD) {
      return pubsSource.filter((pub) => pub.selected);
    }
    if (activeKeyword !== SHOW_ALL_KEYWORD) {
      return pubsSource.filter((pub) =>
        (pub.keywords || []).includes(activeKeyword),
      );
    }
    return pubsSource;
  }, [activeKeyword, pubsSource]);

  const mentalHealthGroups = useMemo(() => {
    return mentalHealthWork.reduce((acc, item) => {
      const existing = acc.find(
        (group) => group.organisation === item.organisation,
      );
      if (existing) {
        existing.items.push(item);
      } else {
        acc.push({ organisation: item.organisation, items: [item] });
      }
      return acc;
    }, []);
  }, []);

  const currentMentoring = mentoringSource.filter((mentee) =>
    mentee.duration.includes("Present"),
  );
  const pastMentoring = mentoringSource.filter(
    (mentee) => !mentee.duration.includes("Present"),
  );
  const currentCollaborators = collaboratorsSource.filter((collab) =>
    collab.duration.includes("Present"),
  );
  const pastCollaborators = collaboratorsSource.filter(
    (collab) => !collab.duration.includes("Present"),
  );
  const renderMenteeName = (mentee) => {
    const fullName = mentee.name || "";
    const firstName = mentee.firstname || "";
    const firstNameStart = fullName.indexOf(firstName);

    if (!firstName || firstNameStart === -1) {
      return fullName;
    }

    const beforeFirstName = fullName.slice(0, firstNameStart);
    const afterFirstName = fullName.slice(firstNameStart + firstName.length);

    return (
      <>
        {beforeFirstName}
        <span className="font-semibold">{firstName}</span>
        {afterFirstName}
      </>
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Sidebar page="Home" />
      <main className="flex-1 py-3 lg:py-4 dark:bg-darkBg dark:text-darkText">
        <PageContainer>
        <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)] lg:gap-x-8 lg:gap-y-4 lg:items-start">
          <section className="belowLg:order-1 lg:contents">
            <div className="flex items-center gap-4 sm:gap-5 lg:contents">
              <div className="min-w-0 flex-1 self-stretch flex flex-col justify-center">
                <EditableRegion regionId="home.identity" label="Name & title">
                  <h1 className="text-2xl xl:text-3xl font-bold text-lightText dark:text-darkText">
                    {copy.displayName}
                  </h1>
                  <p className="mt-2.5 text-base xl:text-lg text-green dark:text-green-dark">
                    {copy.displayTitle}
                  </p>
                  {copy.displayTagline ? (
                    <p className="mt-1 text-xs xl:text-sm text-gray-700 dark:text-gray-300">
                      {copy.displayTagline}
                    </p>
                  ) : null}
                </EditableRegion>
              </div>
              <div className="flex shrink-0 justify-center self-center lg:w-full">
                <img
                  className="h-24 w-24 sm:h-28 sm:w-28 rounded-full object-cover object-[center_18%]"
                  src={kellieMainImg}
                  alt="Kellie Sim"
                />
              </div>
            </div>
          </section>

          <section className="text-left belowLg:order-2 belowLg:border-b belowLg:border-gray-200 belowLg:pb-4 dark:border-gray-700 lg:min-w-0">
            <EditableRegion regionId="home.pitch" label="Home bio">
              <HomeBio
                pitch={pitch}
                pillars={pillarsForCards}
              />
            </EditableRegion>

            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
              {introLinks.map((item, index) => {
                const Icon = iconMap[item.iconKey];
                return (
                  <span key={item.name} className="inline-flex items-center">
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-lightText dark:text-darkText hover:text-green dark:hover:text-green-dark transition-colors"
                    >
                      <Icon size={13} />
                      {item.name}
                    </a>
                    {index < introLinks.length - 1 ? (
                      <span className="mx-2 text-gray-400 dark:text-gray-500">
                        |
                      </span>
                    ) : (
                      ""
                    )}
                  </span>
                );
              })}
            </p>
          </section>

          <aside className="contents text-left lg:block lg:min-w-0 lg:space-y-4">
            <section className="border-b border-gray-200 pb-4 belowLg:order-3 dark:border-gray-700 lg:pt-0">
              <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                News and Updates
              </h2>
              <EditableRegion regionId="home.news" label="News">
                <NewsUpdates />
              </EditableRegion>
            </section>

            <section className="belowLg:order-4 belowLg:border-b belowLg:border-gray-200 belowLg:pb-4 dark:border-gray-700 lg:pt-0">
              <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                Awards and Honours
              </h2>
              <EditableRegion regionId="home.awards" label="Awards">
                <div className="flex flex-col gap-3 pb-3">
                  <ul className="list-disc space-y-1 pl-4 pr-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300 custom-scrollbar">
                    {awards.map((item) => (
                      <li
                        key={item}
                        dangerouslySetInnerHTML={
                          /<a\s/i.test(item) ? { __html: item } : undefined
                        }
                      >
                        {/<a\s/i.test(item) ? null : item}
                      </li>
                    ))}
                  </ul>
                </div>
              </EditableRegion>
            </section>
          </aside>
        </div>

        <ResearchSection />

        <div className="flex flex-col gap-6 border-t border-gray-200 pt-5 dark:border-gray-700 lg:grid lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)] lg:gap-8">
          <div className="contents lg:block lg:space-y-6 lg:min-w-0">
            <section className="text-left belowLg:order-5 belowLg:border-b belowLg:pb-4 dark:border-gray-700">
              <EditableRegion regionId="home.publications" label="Publications / Papers">
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Publications / Papers
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Filter by keyword to explore my publications!
                </p>
                <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    Keywords:
                  </span>
                  {publicationKeywords
                    .filter((keyword) => keyword !== SHOW_ALL_KEYWORD)
                    .map((keyword, idx, arr) => (
                      <span
                        key={keyword}
                        className="inline-flex items-center gap-2"
                      >
                        <button
                          type="button"
                          onClick={() => setActiveKeyword(keyword)}
                          className={`font-medium transition-colors ${
                            activeKeyword === keyword
                              ? "text-green dark:text-green-dark"
                              : "text-gray-500 hover:text-green dark:text-gray-400 dark:hover:text-green-dark"
                          }`}
                        >
                          {keyword}
                        </button>
                        {idx < arr.length - 1 && (
                          <span className="text-gray-300 dark:text-gray-600">
                            |
                          </span>
                        )}
                      </span>
                    ))}
                </div>
                <div className="mb-3 text-sm">
                  <button
                    type="button"
                    onClick={() => setActiveKeyword(SHOW_ALL_KEYWORD)}
                    className={`font-medium transition-colors ${
                      activeKeyword === SHOW_ALL_KEYWORD
                        ? "text-green dark:text-green-dark"
                        : "text-gray-500 hover:text-green dark:text-gray-400 dark:hover:text-green-dark"
                    }`}
                  >
                    Show all
                  </button>
                </div>
                {filteredPublications.map((pub, idx) => (
                  <PublicationCard key={idx} {...pub} />
                ))}
                {filteredPublications.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    No publications found for this filter.
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Acronyms:&nbsp;
                  {Object.entries(acronymTooltips).map(
                    ([acronym, full], i, arr) => (
                      <span key={acronym}>
                        <span className="font-medium">{acronym}</span> - {full}
                        {i < arr.length - 1 ? "; " : "."}
                      </span>
                    ),
                  )}
                </p>
              </EditableRegion>
            </section>

            <section
              id="mental-health"
              className="scroll-mt-20 text-left border-gray-200 pt-0 belowLg:order-6 belowLg:border-b belowLg:pb-4 dark:border-gray-700 lg:border-t lg:pt-5"
            >
              <EditableRegion
                regionId="home.mentalHealth"
                label="Mental health"
              >
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Mental Health Work
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  {copy.mentalHealthBlurb}
                </p>
                <div className="flex flex-col gap-1">
                  {mentalHealthGroups.map((group) => (
                    <div key={group.organisation} className="pt-3 first:pt-2">
                      {group.items.map((item, index) => (
                        <MentalHealthCard
                          key={`${group.organisation}-${item.role}-${index}`}
                          organisation={item.organisation}
                          role={item.role}
                          duration={item.duration}
                          jobscope={item.jobscope}
                          showOrganisation={index === 0}
                          organisationHeading="h3"
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </EditableRegion>
            </section>
          </div>

          <aside className="contents text-left lg:block lg:min-w-0">
            <section className="border-b border-gray-200 pb-4 belowLg:order-8 dark:border-gray-700 lg:pt-0">
              <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                Press and Social Media
              </h2>
              <EditableRegion regionId="home.features" label="Press and Social Media">
                <Features />
              </EditableRegion>
            </section>

            <section className="border-b border-gray-200 pb-4 belowLg:order-9 dark:border-gray-700 lg:pt-5">
              <EditableRegion regionId="home.services" label="Services">
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Academic Services
                </h2>
                <div className="pb-3">
                  <p className="text-base font-semibold text-gray-700 dark:text-gray-300">
                    Reviewing
                  </p>
                  <ul className="mt-1 list-disc space-y-1 pl-4 pr-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300 custom-scrollbar">
                    {(services.reviewing || []).map((item) => {
                      const { awarded, label } = parseAwardedLabel(item);
                      return (
                        <li key={item}>
                          <AwardedLabel awarded={awarded}>{label}</AwardedLabel>
                        </li>
                      );
                    })}
                  </ul>
                  {services.specialRecognition ? (
                    <p className="mt-1 text-sm leading-relaxed text-amber-700 dark:text-amber-300 font-medium">
                      <AwardedLabel awarded medalPosition="start">
                        {parseAwardedLabel(services.specialRecognition).label}
                      </AwardedLabel>
                    </p>
                  ) : null}
                  <p className="mt-3 text-base font-semibold text-gray-700 dark:text-gray-300">
                    Student Volunteer
                  </p>
                  <ul className="mt-1 list-disc space-y-1 pl-4 pr-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300 custom-scrollbar">
                    {(services.studentVolunteer || []).map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </EditableRegion>
            </section>

            <section className="border-b border-gray-200 pb-4 belowLg:order-7 dark:border-gray-700 lg:pt-5">
              <EditableRegion regionId="home.people" label="People">
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Mentoring and Collaborators
                </h2>

                <p className="text-base font-semibold text-gray-700 dark:text-gray-300 mt-3">
                  Mentoring
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Current
                </p>
                <ul className="mt-1 list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                  {currentMentoring.map((mentee) => (
                    <li key={mentee.id}>
                      {renderMenteeName(mentee)} ({mentee.designation};{" "}
                      {mentee.duration})
                    </li>
                  ))}
                </ul>
                {pastMentoring.length > 0 && (
                  <>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Past
                    </p>
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                      {pastMentoring.map((mentee) => (
                        <li key={mentee.id}>
                          {renderMenteeName(mentee)} ({mentee.designation};{" "}
                          {mentee.duration})
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <p className="text-base font-semibold text-gray-700 dark:text-gray-300 mt-4">
                  Collaborators
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Current
                </p>
                <ul className="mt-1 list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                  {currentCollaborators.map((collab) => (
                    <li key={collab.name}>
                      {renderMenteeName(collab)} ({collab.designation})
                    </li>
                  ))}
                </ul>
                {pastCollaborators.length > 0 && (
                  <>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Past
                    </p>
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                      {pastCollaborators.map((collab) => (
                        <li key={collab.name}>
                          {renderMenteeName(collab)} ({collab.designation};{" "}
                          {collab.duration})
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </EditableRegion>
            </section>

            <section className="border-gray-200 pb-0 belowLg:order-10 dark:border-gray-700 lg:pt-5">
              <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                Personal Interests
              </h2>
              <EditableRegion regionId="home.interests" label="Interests">
                <div className="flex flex-col gap-3 pb-3">
                  {interests.map((paragraph, idx) => (
                    <p
                      key={idx}
                      className="text-sm leading-relaxed text-gray-700 dark:text-gray-300"
                      dangerouslySetInnerHTML={
                        /<a\s/i.test(paragraph)
                          ? { __html: paragraph }
                          : undefined
                      }
                    >
                      {/<a\s/i.test(paragraph) ? null : paragraph}
                    </p>
                  ))}
                </div>
              </EditableRegion>
            </section>
          </aside>
        </div>
        </div>
        </PageContainer>
      </main>
      <Footer />
    </div>
  );
};

export default Home;
